/**
 * Combien vous verser ce mois-ci, sans que votre société manque d'argent.
 *
 * LA PEUR DU GÉRANT SEUL, C'EST LA FACTURE QU'IL N'A PAS VUE VENIR. Le solde du
 * compte de la société ment : une partie appartient déjà à l'État (la TVA
 * encaissée), une autre lui appartiendra à la clôture (l'impôt sur les
 * sociétés du bénéfice fait depuis janvier), et une troisième partira à
 * l'Urssaf. Ce module retire tout cela du solde, puis cherche la rémunération
 * nette la plus haute que la société peut encore payer, cotisations comprises.
 *
 * LA RÉMUNÉRATION SE PAIE DEUX FOIS, ET L'IMPÔT UNE FOIS EN MOINS. Chaque euro
 * versé coûte à la société ses cotisations en plus, mais réduit d'autant le
 * bénéfice sur lequel elle paiera l'impôt. Le calcul tient les deux : c'est ce
 * qui le rend exact là où la règle du pouce « gardez 25 % de côté » se trompe
 * dans les deux sens.
 *
 * TOUT SE COMPTE SUR L'EXERCICE EN COURS. Les cotisations sont celles du
 * barème annuel appliqué à tout ce que le dirigeant s'est versé depuis
 * l'ouverture de l'exercice, ce mois compris ; l'impôt est celui du bénéfice
 * fait depuis la même date. En SASU, les cotisations des salaires déjà versés
 * sont réputées payées, parce qu'elles partent avec chaque paie ; en EURL, le
 * lecteur dit ce qu'il a déjà réglé à l'Urssaf, et la différence reste à
 * prévoir, régularisation comprise.
 *
 * LES DIVIDENDES N'ENTRENT PAS ICI. Un dividende ne se verse qu'une fois les
 * comptes de l'exercice approuvés : ce n'est pas un versement du mois. L'écran
 * renvoie à l'outil du partage.
 *
 * Les barèmes sont ceux du comparateur (businessStructure.js) : les deux outils
 * ne peuvent pas se contredire sur le coût d'une rémunération.
 */

import {
    cotisationsAssimileSalarie,
    cotisationsIndependant,
    impotSocietes,
} from "./businessStructure.js";

export const FORMES_VERSEMENT = ["eurl", "sasu"];

const positif = (v) => Math.max(0, v);

/** Le nombre de tours des recherches par dichotomie : bien en deçà du centime. */
const TOURS = 60;

/**
 * Ce que coûte à la société une rémunération nette avant impôt, sur
 * l'exercice : le coût total, les cotisations qu'il contient, et en SASU le
 * salaire brut correspondant.
 *
 * Le chemin va du net au coût, à l'inverse des barèmes : on cherche le brut
 * (SASU) ou l'enveloppe (EURL) qui laisse ce net. En EURL, les cotisations
 * minimales restent dues à zéro euro de rémunération : la société les paie, et
 * le coût ne tombe jamais à zéro.
 */
export function coutPourNet({ forme, net, activite = "services", artisan = false }) {
    const cible = positif(net);
    if (forme === "sasu") {
        if (cible === 0) return { cout: 0, cotisations: 0, brut: 0 };
        let bas = cible;
        let haut = cible * 2 + 1000;
        for (let i = 0; i < TOURS; i++) {
            const milieu = (bas + haut) / 2;
            if (cotisationsAssimileSalarie(milieu).net < cible) bas = milieu;
            else haut = milieu;
        }
        const c = cotisationsAssimileSalarie(haut);
        return { cout: c.coutEmployeur, cotisations: c.coutEmployeur - c.net, brut: haut };
    }

    const netDe = (enveloppe) =>
        positif(enveloppe - cotisationsIndependant({ revenuAvantCotisations: enveloppe, activite, artisan }).total);
    let enveloppe = 0;
    if (cible > 0) {
        let bas = 0;
        let haut = cible * 3 + 10000;
        for (let i = 0; i < TOURS; i++) {
            const milieu = (bas + haut) / 2;
            if (netDe(milieu) < cible) bas = milieu;
            else haut = milieu;
        }
        enveloppe = haut;
    }
    const c = cotisationsIndependant({ revenuAvantCotisations: enveloppe, activite, artisan });
    return { cout: Math.max(enveloppe, c.total), cotisations: c.total, brut: null };
}

/**
 * La rémunération nette la plus haute que la société peut verser ce mois-ci.
 *
 * @param forme          « eurl » (à l'impôt sur les sociétés) ou « sasu »
 * @param solde          ce qu'il y a sur le compte de la société aujourd'hui
 * @param tvaAReverser   TVA encaissée pas encore reversée, déductible retirée
 * @param benefice       bénéfice de l'exercice jusqu'ici, AVANT toute
 *                       rémunération du dirigeant et ses cotisations
 * @param dejaVerse      rémunération nette avant impôt déjà versée sur
 *                       l'exercice
 * @param cotisationsPayees EURL : cotisations déjà réglées à l'Urssaf sur
 *                       l'exercice ; ignoré en SASU
 * @param acomptesIs     acomptes d'impôt sur les sociétés déjà payés pour
 *                       l'exercice
 * @param coussin        ce que le dirigeant veut laisser sur le compte
 */
export function versementPossible({
    forme,
    activite = "services",
    artisan = false,
    solde,
    tvaAReverser = 0,
    benefice,
    dejaVerse = 0,
    cotisationsPayees = 0,
    acomptesIs = 0,
    coussin = 0,
}) {
    const commun = { forme, activite, artisan };
    const deja = coutPourNet({ ...commun, net: dejaVerse });
    // En SASU, les cotisations des paies passées sont parties avec elles.
    const payees = forme === "sasu" ? deja.cotisations : positif(cotisationsPayees);

    const etat = (versement) => {
        const total = coutPourNet({ ...commun, net: dejaVerse + versement });
        const cotisations = positif(total.cotisations - payees);
        const impot = positif(impotSocietes(benefice - total.cout) - positif(acomptesIs));
        const reste = solde - positif(tvaAReverser) - impot - cotisations - versement - positif(coussin);
        return { versement, total, cotisations, impot, reste };
    };

    const sansRien = etat(0);
    let retenu = sansRien;
    if (sansRien.reste > 0) {
        // Le reste baisse quand le versement monte : chaque euro versé coûte
        // au moins un euro, et l'impôt n'en rend jamais plus d'un quart.
        let bas = 0;
        let haut = positif(solde);
        for (let i = 0; i < TOURS; i++) {
            const milieu = (bas + haut) / 2;
            if (etat(milieu).reste >= 0) bas = milieu;
            else haut = milieu;
        }
        retenu = etat(bas);
    }

    return {
        versement: retenu.versement,
        // En SASU, le brut de CE mois : celui de l'exercice, moins celui des
        // paies déjà faites.
        brut: forme === "sasu" ? positif(retenu.total.brut - deja.brut) : null,
        tva: positif(tvaAReverser),
        impot: retenu.impot,
        cotisations: retenu.cotisations,
        coussin: positif(coussin),
        // Ce qui manque quand le solde ne couvre même pas ce que la société
        // doit déjà, sans rien verser : le lecteur doit le lire en premier.
        manque: sansRien.reste < 0 ? -sansRien.reste : 0,
        coutDuVersement: retenu.total.cout - deja.cout,
    };
}
